/**
 * saleRules.js
 * 
 * What happens when the host sells an item: whether the sale is allowed,
 * and where everyone goes next.
 */

import { getTeamItems } from "../library/libraries";
import { endOfAuctionFields } from "./auctionLobby";

/**
 * Whether a price is allowed by the bid rules: at least the starting bid, and a whole number of bid steps above it
 * @param {Number} amount   -   Price
 * @param {Object} settings -   Room settings
 * @returns {String|null}   -   Why the price isn't allowed, or null if it is
 */
export const getBidProblem = (amount, settings) => {
    if (!Number.isInteger(amount) || amount <= 0)        return "Enter a whole amount above 0.";
    if (amount < settings.startingBid)                   return `Bids start at ₹${settings.startingBid}.`;
    if ((amount - settings.startingBid) % settings.bidStep !== 0) {
        const lower = amount - ((amount - settings.startingBid) % settings.bidStep);
        return `Bids go up in steps of ₹${settings.bidStep} from ₹${settings.startingBid}: try ₹${lower} or ₹${lower + settings.bidStep}.`;
    }
    return null;
};

/**
 * The nearest allowed price at or above an amount
 * @param {Number} amount   -   Price
 * @param {Object} settings -   Room settings
 * @returns {Number}
 */
export const roundUpToBid = (amount, settings) =>
    amount <= settings.startingBid
        ? settings.startingBid
        : settings.startingBid + Math.ceil((amount - settings.startingBid) / settings.bidStep) * settings.bidStep;

/**
 * Why a team can't buy anything right now (full, or can't afford the starting bid), or null if it can
 * @param {Object} team     -   Team
 * @param {Object} settings -   Room settings
 * @returns {String|null}
 */
export const getTeamBuyingProblem = (team, settings) => {
    if (getTeamItems(team).length >= settings.maxItemsPerTeam) return `${team.name} already has the maximum of ${settings.maxItemsPerTeam} items.`;
    if ((team.budgetLeft ?? 0) < settings.startingBid)          return `${team.name} can't afford the starting bid of ₹${settings.startingBid}.`;
    return null;
};

/**
 * Checks a sale against the lobby settings
 * @returns {String|null}   -   Why the sale isn't allowed, or null if it is
 */
export const getSaleProblem = (team, item, amount, settings) => {
    const teamItems = getTeamItems(team);

    const bidProblem = getBidProblem(amount, settings);
    if (bidProblem)                                     return bidProblem;
    if (amount > (team.budgetLeft ?? 0))                return `${team.name} only has ₹${team.budgetLeft ?? 0} left.`;
    if (teamItems.length >= settings.maxItemsPerTeam)   return `${team.name} already has the maximum of ${settings.maxItemsPerTeam} items.`;

    // e.g. at most 2 items with the same Actor
    for (const [attribute, rule] of Object.entries(settings.attributeRules)) {
        const value = item.values?.[attribute];
        if (rule.maxPerValue === null || rule.maxPerValue === undefined || value === undefined || value === "") continue;
        const count = teamItems.filter((teamItem) => teamItem.values?.[attribute] === value).length;
        if (count >= rule.maxPerValue) return `${team.name} already has ${rule.maxPerValue} with ${attribute} "${value}".`;
    }
    return null;
};

/**
 * The order items come up in for the current round.
 * Round 1 is the whole library; round 2 (the comeback round) is the items skipped in round 1.
 * @param {Object} room         -   The room
 * @param {Number} itemCount    -   Number of items in the library
 * @returns {Array}             -   Item indices, in order
 */
export const getRoundOrder = (room, itemCount) =>
    room.round === 2
        ? [...(room.unsoldItems || [])].sort((a, b) => a - b)
        : Array.from({ length: itemCount }, (_, index) => index);

/**
 * Room fields to set in the same write as selling or skipping an item:
 * - the next item in this round
 * - after the last item: the comeback round (if the host brings back unsold items), then the submission step or the results
 * - after a sale: a break, if the host asked for one after this many sales
 * - after a skip in round 1: the item is remembered as unsold
 * @param {Object} room         -   The room (before the sale or skip)
 * @param {Object} settings     -   Room settings
 * @param {Number} itemIndex    -   Index of the item being sold or skipped
 * @param {Number} itemCount    -   Number of items in the library
 * @param {Boolean} sold        -   True for a sale, false for a skip
 * @returns {Object}            -   Fields to set on the room
 */
export const getRoomUpdatesAfterItem = (room, settings, itemIndex, itemCount, sold = true) => {
    const round = room.round === 2 ? 2 : 1;
    const unsoldItems = !sold && round === 1
        ? [...new Set([...(room.unsoldItems || []), itemIndex])]
        : (room.unsoldItems || []);
    const updates = !sold && round === 1 ? { unsoldItems } : {};

    const order = getRoundOrder({ ...room, unsoldItems }, itemCount);
    const nextIndex = order[order.indexOf(itemIndex) + 1];

    if (nextIndex !== undefined) {
        updates.currentMovieIndex = nextIndex;
    } else if (round === 1 && settings.bringBackUnsold && unsoldItems.length > 0) {
        updates.round = 2;
        updates.currentMovieIndex = [...unsoldItems].sort((a, b) => a - b)[0];
    } else {
        return { ...updates, ...endOfAuctionFields(settings) };
    }

    const itemsSoldAfterThis = (room.moviesSold || 0) + (sold ? 1 : 0);
    updates.onBreak = sold && settings.breakAfter > 0 && itemsSoldAfterThis % settings.breakAfter === 0;
    return updates;
};
